// services/trainingService.js — Training Roster CRUD + Reminder Emails
import { getSiteId, fetchAllPages, GRAPH_PAGE_SIZE } from "./sharepointService";
import { mailSendRequest } from "../authConfig";

const STORAGE_KEY  = "lekoil_training_roster";
const SP_LIST      = "SCMTrainingRoster";
const graphBase    = "https://graph.microsoft.com/v1.0";

function generateId() {
  return `tr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function toEntry(fields, itemId) {
  return {
    id:           String(itemId || fields.id),
    _spItemId:    itemId || fields.id,
    date:         (fields.TrainingDate || "").split("T")[0],
    time:         fields.TrainingTime  || "",
    handlerName:  fields.HandlerName   || "",
    handlerEmail: fields.HandlerEmail  || "",
    handlerDept:  fields.HandlerDept   || "",
    topic:        fields.Title         || "",
    venue:        fields.Venue         || "",
    notes:        fields.Notes         || "",
    createdBy:    fields.CreatedBy     || "",
    createdAt:    fields.Created       || new Date().toISOString(),
  };
}

function toFields(e) {
  return {
    Title:        e.topic,
    TrainingDate: e.date,
    TrainingTime: e.time  || "",
    HandlerName:  e.handlerName,
    HandlerEmail: e.handlerEmail,
    HandlerDept:  e.handlerDept,
    Venue:        e.venue || "",
    Notes:        e.notes || "",
  };
}

export async function fetchTrainingRoster(token) {
  const siteId = await getSiteId(token);
  const raw = await fetchAllPages(
    token,
    `${graphBase}/sites/${siteId}/lists/${SP_LIST}/items?expand=fields&$top=${GRAPH_PAGE_SIZE}`,
    { errorMessage: "Failed to fetch training roster" },
  );
  return raw.map(i => toEntry(i.fields, i.id));
}

export async function createTrainingEntry(token, entry) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SP_LIST}/items`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ fields: toFields(entry) }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Create failed (${res.status})`);
  }
  const item = await res.json();
  return toEntry(item.fields, item.id);
}

export async function updateTrainingEntry(token, spItemId, entry) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SP_LIST}/items/${spItemId}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ fields: toFields(entry) }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Update failed (${res.status})`);
  }
}

export async function deleteTrainingEntry(token, spItemId) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${SP_LIST}/items/${spItemId}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Delete failed (${res.status})`);
  }
}

// ── localStorage helpers ───────────────────────────────────────────────────────
export function loadLocalRoster() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export function saveLocalRoster(entries) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); } catch {}
}

export function makeLocalEntry(fields, currentUserEmail) {
  return {
    id:           generateId(),
    _spItemId:    null,
    date:         fields.date,
    time:         fields.time  || "",
    handlerName:  fields.handlerName,
    handlerEmail: fields.handlerEmail,
    handlerDept:  fields.handlerDept,
    topic:        fields.topic,
    venue:        fields.venue || "",
    notes:        fields.notes || "",
    createdBy:    currentUserEmail || "",
    createdAt:    new Date().toISOString(),
  };
}

// ── Reminder Email ─────────────────────────────────────────────────────────────
export async function sendTrainingReminder(instance, accounts, entry, senderName) {
  let token;
  try {
    const r = await instance.acquireTokenSilent({ ...mailSendRequest, account: accounts[0] });
    token = r.accessToken;
  } catch {
    const r = await instance.acquireTokenPopup({ ...mailSendRequest, account: accounts[0] });
    token = r.accessToken;
  }

  const dateFmt = new Date(entry.date + "T12:00:00").toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  // Format time to 12-hour for display
  function fmtTime(t) {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12  = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  }
  const timeFmt = fmtTime(entry.time);

  const bodyHtml = `
<div style="font-family:Segoe UI,Arial,sans-serif;max-width:640px;margin:0 auto;background:#fff;">
  <div style="background:linear-gradient(135deg,#001A3D,#002D7A);padding:28px 32px;border-radius:12px 12px 0 0;">
    <div style="color:#fff;font-size:20px;font-weight:700;">Safety Moment & KSS Reminder</div>
    <div style="color:#C8D8FF;font-size:13px;margin-top:4px;">LekOil SCM — Safety Moment & KSS Roster</div>
  </div>
  <div style="padding:28px 32px;border:1px solid #E0E7FF;border-top:none;border-radius:0 0 12px 12px;">
    <p style="margin:0 0 20px;font-size:14px;color:#201F1E;">
      Dear <strong>${escHtml(entry.handlerName)}</strong>,
    </p>
    <p style="margin:0 0 20px;font-size:14px;color:#333;line-height:1.7;">
      This is a friendly reminder that you are scheduled to <strong>present the Safety Moment / KSS session</strong> with the following details:
    </p>

    <!-- Session details block -->
    <div style="background:#EBF3FF;border-left:4px solid #0066FF;border-radius:8px;padding:20px 24px;margin:0 0 24px;">
      <div style="font-size:18px;font-weight:700;color:#001A3D;margin-bottom:12px;">📅 ${escHtml(dateFmt)}</div>
      ${timeFmt ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;"><span style="font-size:15px;">🕐</span><span style="font-size:15px;font-weight:600;color:#001A3D;">${escHtml(timeFmt)}</span></div>` : ""}
      ${entry.venue ? `<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:15px;">📍</span><span style="font-size:14px;color:#333;">${escHtml(entry.venue)}</span></div>` : ""}
    </div>

    <!-- Training details table -->
    <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
      <tr>
        <td style="padding:11px 14px;background:#F8F9FF;border:1px solid #E0E7FF;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;letter-spacing:0.5px;width:28%;">Topic / Subject</td>
        <td style="padding:11px 14px;border:1px solid #E0E7FF;border-left:none;font-size:14px;color:#201F1E;font-weight:600;">${escHtml(entry.topic)}</td>
      </tr>
      ${entry.venue ? `
      <tr>
        <td style="padding:11px 14px;background:#F8F9FF;border:1px solid #E0E7FF;border-top:none;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;letter-spacing:0.5px;">Venue</td>
        <td style="padding:11px 14px;border:1px solid #E0E7FF;border-left:none;border-top:none;font-size:14px;color:#201F1E;">${escHtml(entry.venue)}</td>
      </tr>` : ""}
      ${timeFmt ? `
      <tr>
        <td style="padding:11px 14px;background:#F8F9FF;border:1px solid #E0E7FF;border-top:none;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;letter-spacing:0.5px;">Time</td>
        <td style="padding:11px 14px;border:1px solid #E0E7FF;border-left:none;border-top:none;font-size:14px;color:#201F1E;">${escHtml(timeFmt)}</td>
      </tr>` : ""}
      ${entry.notes ? `
      <tr>
        <td style="padding:11px 14px;background:#F8F9FF;border:1px solid #E0E7FF;border-top:none;font-size:11px;font-weight:700;color:#7A86A0;text-transform:uppercase;letter-spacing:0.5px;">Notes</td>
        <td style="padding:11px 14px;border:1px solid #E0E7FF;border-left:none;border-top:none;font-size:14px;color:#201F1E;line-height:1.6;">${escHtml(entry.notes)}</td>
      </tr>` : ""}
    </table>

    <p style="margin:0 0 24px;font-size:13px;color:#555;line-height:1.6;">
      Please ensure you are fully prepared for the session. If you have any questions or need to reschedule, kindly contact the SCM team as soon as possible.
    </p>
    <p style="margin:0;font-size:13px;color:#555;">
      Best regards,<br>
      <strong>${escHtml(senderName)}</strong><br>
      SCM Department, LekOil
    </p>
  </div>
</div>`;

  const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      message: {
        subject: `Training Reminder: You are handling training on ${dateFmt}`,
        body: { contentType: "HTML", content: bodyHtml },
        toRecipients: [{ emailAddress: { address: entry.handlerEmail, name: entry.handlerName } }],
      },
      saveToSentItems: true,
    }),
  });

  if (!res.ok && res.status !== 202) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Mail send failed (HTTP ${res.status})`);
  }
}

function escHtml(str) {
  if (!str) return "";
  return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
